import React, { useMemo, useState } from "react";
import { debounce, get } from "lodash";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import { currencyFormatter } from "../../utils/currencyFormat";
import { useGetProductsHandler } from "../../resources/products/dataHandlers/getProductsHandler";

const ProductSelect = ({
  label = "Item Name",
  name,
  control,
  errors,
  helperText,
  readOnly = false,
  onProductSelect,
  onNameChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const modifySearchTerm = useMemo(
    () =>
      debounce((value) => {
        setSearchTerm(value.trim());
      }, 300),
    [],
  );

  const { products, isLoading } = useGetProductsHandler({
    searchQuery: {
      page: 0,
      size: 10,
      name: searchTerm || null,
    },
  });

  const errorMessage = get(errors, name, {})?.message;
  const combinedHelperText = [helperText, errorMessage]
    .filter(Boolean)
    .join(" * ");

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Autocomplete
            freeSolo
            loading={isLoading}
            {...field}
            id={name}
            fullWidth
            readOnly={readOnly}
            options={products}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.name || ""
            }
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box
                  key={key}
                  component="li"
                  sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                  {...optionProps}
                >
                  {option?.name || ""}{" "}
                  {option?.unit_price != null
                    ? " | " + currencyFormatter().format(option.unit_price)
                    : ""}
                </Box>
              );
            }}
            getOptionKey={(option) => option?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!errorMessage}
                helperText={combinedHelperText}
                onChange={(e) => onNameChange(e.target.value)}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => {
              const finalValue =
                value === null
                  ? null
                  : typeof value === "string"
                    ? { name: value }
                    : value;
              field.onChange(
                typeof finalValue === "object" && finalValue?.name
                  ? finalValue.name
                  : finalValue,
              );
              if (onProductSelect) onProductSelect(finalValue);
            }}
            onInputChange={(event, value, reason) => {
              if (reason === "input") {
                modifySearchTerm(value);
                onNameChange?.(value);
              }
            }}
            value={field.value || ""}
          />
        </Box>
      )}
    />
  );
};

export default ProductSelect;
